import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Segmented range control (DESIGN-SPEC.md §4.4 "Filter pills & segmented
 * controls")
 *
 * New for the Institutional Console redesign (issue #233). Used for chart
 * time-range switches (1M/3M/YTD/1Y/All) on Dashboard/Analytics. Container:
 * `rgba(255,255,255,0.04)` bg, 6px radius, 2px padding. Active segment:
 * `#1c344f` flat navy chip (dark theme) / white bg + light card shadow
 * (light theme) — see `--segmented-active-bg` (added in
 * `styles/themes/console-dark.css` / `console-light.css`) and the
 * `segmented-active` Tailwind color key (`tailwind.config.js`).
 *
 * Implemented with `role="radiogroup"`/`role="radio"` (single-select,
 * mutually exclusive range options) rather than the mockup's plain `<span>`
 * list, per the WAI-ARIA Radio Group Pattern
 * (https://www.w3.org/WAI/ARIA/apg/patterns/radio/): roving tabindex (only
 * the selected option is Tab-stoppable) PLUS an explicit ArrowLeft/
 * ArrowRight/ArrowUp/ArrowDown/Home/End keydown handler on the group that
 * moves focus AND selection together (selection follows focus in this
 * pattern — there is no separate "confirm" step). This handler is required
 * because native roving-tabindex-only behavior is a browser feature of real
 * `<input type="radio">` elements; it does NOT apply automatically to
 * ARIA-role `<button>`s, which is why the keydown handler below exists
 * (round-2 fix — system-architect review F2 / ui-design-expert review F1:
 * an earlier revision of this component claimed arrow navigation "for free"
 * without implementing it, shipping a keyboard-inoperable control).
 *
 * @example
 * <SegmentedControl value={range} onValueChange={setRange} aria-label="Chart time range">
 *   <SegmentedControlOption value="1M">1M</SegmentedControlOption>
 *   <SegmentedControlOption value="3M">3M</SegmentedControlOption>
 *   <SegmentedControlOption value="YTD">YTD</SegmentedControlOption>
 *   <SegmentedControlOption value="1Y">1Y</SegmentedControlOption>
 *   <SegmentedControlOption value="All">All</SegmentedControlOption>
 * </SegmentedControl>
 */
const SegmentedControlContext = React.createContext(null);

const SegmentedControl = React.forwardRef(
  ({ className, value, onValueChange, children, ...props }, ref) => {
    const groupRef = React.useRef(null);
    React.useImperativeHandle(ref, () => groupRef.current);

    // APG radio-group keyboard contract: ArrowRight/ArrowDown -> next option
    // (wrap to first); ArrowLeft/ArrowUp -> previous (wrap to last);
    // Home/End -> first/last. Selection follows focus (no separate Enter/
    // Space "confirm" step is required by the pattern, though activating
    // the focused option's onClick would be redundant here since focus
    // itself already changes the value).
    const handleKeyDown = (event) => {
      const key = event.key;
      const isNext = key === "ArrowRight" || key === "ArrowDown";
      const isPrev = key === "ArrowLeft" || key === "ArrowUp";
      const isHome = key === "Home";
      const isEnd = key === "End";
      if (!isNext && !isPrev && !isHome && !isEnd) return;

      const group = groupRef.current;
      if (!group) return;
      const options = Array.from(group.querySelectorAll('[role="radio"]')).filter(
        (el) => !el.disabled
      );
      if (options.length === 0) return;

      event.preventDefault();

      const currentIndex = options.findIndex((el) => el.getAttribute("aria-checked") === "true");
      let nextIndex;
      if (isHome) {
        nextIndex = 0;
      } else if (isEnd) {
        nextIndex = options.length - 1;
      } else if (isNext) {
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
      } else {
        nextIndex = currentIndex < 0 ? options.length - 1 : (currentIndex - 1 + options.length) % options.length;
      }

      const nextOption = options[nextIndex];
      const nextValue = nextOption.getAttribute("data-value");
      onValueChange?.(nextValue);
      // Move DOM focus synchronously so repeated arrow presses keep working
      // even before the controlled `value` prop re-renders with the new
      // tabIndex=0 target.
      nextOption.focus();
    };

    return (
      <SegmentedControlContext.Provider value={{ value, onValueChange }}>
        <div
          ref={groupRef}
          role="radiogroup"
          onKeyDown={handleKeyDown}
          className={cn(
            "inline-flex items-center gap-0.5 rounded-md bg-[rgba(255,255,255,0.04)] p-0.5 text-[11.5px]",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </SegmentedControlContext.Provider>
    );
  }
);
SegmentedControl.displayName = "SegmentedControl";

const SegmentedControlOption = React.forwardRef(
  ({ className, value, children, ...props }, ref) => {
    const ctx = React.useContext(SegmentedControlContext);
    const isActive = ctx?.value === value;

    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={isActive}
        data-value={value}
        tabIndex={isActive ? 0 : -1}
        onClick={() => ctx?.onValueChange?.(value)}
        className={cn(
          // N2 (ui-design-expert round-1): px-2 (8px) -> px-[9px] to match
          // the mockup's `padding: 3px 9px` (Dashboard.dc.html:59,
          // Analytics.dc.html:69) exactly.
          "rounded-[4px] px-[9px] py-[3px] font-medium transition-colors duration-[120ms] ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
          isActive
            ? "bg-segmented-active text-foreground shadow-card"
            : "text-[var(--text-muted,var(--color-text-tertiary))] hover:text-foreground",
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
SegmentedControlOption.displayName = "SegmentedControlOption";

export { SegmentedControl, SegmentedControlOption };
